import "../pages/ExcelDiffPage.css";

import React, {memo, useMemo} from "react";
import {AgGridReact} from 'ag-grid-react';
import {
    ColDef,
    ColGroupDef,
    GridOptions,
    themeQuartz,
    ValueGetterParams,
    RowClassRules,
    CellClassRules,
    ModuleRegistry,
    RowStyleModule,
    CellStyleModule,
    TextFilterModule,
    NumberFilterModule,
    CustomFilterModule,
    ClientSideRowModelModule,
} from 'ag-grid-community';
import {
    SetFilterModule,
    MultiFilterModule,
} from 'ag-grid-enterprise';

import {CompareDFsResult} from "../types";
import {cellFormatter, computeColumnSizes, DocumentLinkComponent, ProductLinkComponent} from "shared/components/table";
import {DfEntryStatus} from "shared/types/common";

ModuleRegistry.registerModules([
    RowStyleModule,
    CellStyleModule,
    TextFilterModule,
    NumberFilterModule,
    CustomFilterModule,
    ClientSideRowModelModule,
    SetFilterModule,
    MultiFilterModule,
]);

const ExcelTable = ({result}: {result: CompareDFsResult}) => {
    const rowData = useMemo(() => {return result.rows}, [result]);
    const columnSizes = useMemo(() => computeColumnSizes(result.columns.map(c => c.name), result.rows), [result]);

    const ragCellClassRules: CellClassRules = useMemo(() => {
        return {
            // "rag-extra-green": (classParams) => classParams.data.status === DfEntryStatus.New && classParams.data.changedCols?.includes(classParams.colDef.field ?? classParams.colDef.headerName) && !classParams.colDef.cellClass,
            "rag-extra-red": (classParams) => classParams.data.status === DfEntryStatus.Deprecated && classParams.data.changedCols?.includes(classParams.colDef.field ?? classParams.colDef.headerName) && !classParams.colDef.cellClass,
            "rag-extra-blue": (classParams) => classParams.data.status === DfEntryStatus.Modified && classParams.data.changedCols?.includes(classParams.colDef.field ?? classParams.colDef.headerName) && !classParams.colDef.cellClass,
        };
    }, []);

    const colDefs: (ColDef<any, any> | ColGroupDef<any>)[] = useMemo(() => { return [
        {
            headerName: "Row",
            valueGetter: (p: ValueGetterParams) => p.data.Row + ', ' + p.data.URL,
            cellRenderer: ProductLinkComponent,
            filter: false,
            comparator: (valueA, valueB) => valueA.split(', ')[0] - valueB.split(', ')[0],
            flex: columnSizes['Row']
        },
        ...result.columns
            .filter(c => c.name !== "Row" && c.name !== "URL" && c.name !== "imageFile" && !c.name.includes('Sheet'))
            .map(c => {
            return {
                field: c.name,
                valueFormatter: cellFormatter,
                cellClass: c.status === DfEntryStatus.New? "rag-green" : c.status === DfEntryStatus.Deprecated? "rag-red" : undefined,
                filter: true,
                flex: columnSizes[c.name],
                cellClassRules: ragCellClassRules
            }
        }),
        ...result.columns
            .filter(c => c.name.includes('Sheet') || c.name === "imageFile")
            .map(c => {
            return {
                field: c.name,
                cellRenderer: DocumentLinkComponent,
                cellClass: c.status === DfEntryStatus.New? "rag-green" : c.status === DfEntryStatus.Deprecated? "rag-red" : undefined,
                headerClass: c.status === DfEntryStatus.New? "rag-green" : c.status === DfEntryStatus.Deprecated? "rag-red" : undefined,
                filter: true,
                flex: columnSizes[c.name],
                cellClassRules: ragCellClassRules
            }
        }),
    ]}, [result, columnSizes, ragCellClassRules]);

    const tableTheme = themeQuartz.withParams({
        spacing: 3,
        foregroundColor: 'white',
        backgroundColor: '#27374D',
        headerBackgroundColor: '#354A5F',
        rowHoverColor: '#496F87',
    });

    const gridOptions: GridOptions =  {
        defaultColDef: {
            flex: 1,
            sortable: true,
            filter: true,
        },
        enableFillHandle: true,
        enableCellTextSelection: true,
        suppressHorizontalScroll: false,
    };

    const rowClassRules = useMemo<RowClassRules>(() => {
        return {
            "rag-red": (params) => params.data.status === DfEntryStatus.Deprecated,
            "rag-green": (params) => params.data.status === DfEntryStatus.New,
            "rag-blue": (params) => params.data.status === DfEntryStatus.Modified,
        };
    }, []);

    return (
        <div className="excel-table-container scrollable" style={{width: columnSizes['generalLength']*8}}> {/**/}
            <AgGridReact
                rowData={rowData}
                columnDefs={colDefs}
                gridOptions={gridOptions}
                theme={tableTheme}
                rowClassRules={rowClassRules}
              />
        </div>
    );
}

export default memo(ExcelTable);